import React, { useEffect, useRef, useMemo, useState } from 'react'
import * as THREE from 'three'
import { Canvas, useFrame } from '@react-three/fiber'
import {
  Environment,
  useGLTF,
  Stats,
  PerspectiveCamera
} from '@react-three/drei'
import styles from './clusterCannon.m.scss'
import {
  EffectComposer,
  SSAO as Ssao,
  Noise
} from '@react-three/postprocessing'
// import { LayerMaterial, Depth, Fresnel } from 'lamina'
// import { Leva, useControls } from 'leva'
import { animated, config } from '@react-spring/three'
import { useSpring } from 'react-spring'

const isMobile = window.innerWidth < 768

const Cube = ({ letter, position, stage }) => {
  const cubeAssets = useGLTF(require('../cubes.glb'))
  const delta = useRef(0)
  const cubes = useMemo(
    () => ({
      cube: cubeAssets.nodes.cube,
      letters: cubeAssets.nodes.letters
    }),
    [cubeAssets]
  )
  const [activeLetter, setActiveLetter] = useState(letter)
  const rd = useMemo(() => 1 - Math.random() * 2, [])
  const cube = useRef()

  const letterPositions = new Map([
    ['A', [0, 0, 0]],
    ['E', [0, Math.PI, 0]],
    ['I', [Math.PI * 0.5, Math.PI * -0.5, 0]],
    ['O', [0, Math.PI * 0.5, 0]],
    ['L', [Math.PI * 1.5, Math.PI * 0.5, 0]],
    ['M', [0, Math.PI * 1.5, 0]]
  ])

  const rotation = letterPositions.get(activeLetter)

  const [springRotation, springRotationApi] = useSpring(
    {
      rotation: rotation,
      config: config.wobbly
    },
    []
  )

  const [springPosition, springPositionApi] = useSpring(
    {
      position: position,
      config: config.gentle
    },
    []
  )

  useFrame(state => {
    delta.current += 0.01

    cube.current.rotation.x +=
      Math.sin(state.clock.elapsedTime * 0.8) * rd * 0.01
    cube.current.rotation.y +=
      Math.sin(state.clock.elapsedTime * 0.8) * rd * 0.01
  })

  useEffect(() => {
    setActiveLetter(letter)
  }, [letter])

  useEffect(() => {
    delta.current = 0
    springRotationApi.start({ rotation: rotation })
  }, [activeLetter])

  useEffect(() => {
    springPositionApi.start({ position: position })
  }, [position])

  return (
    <animated.group
      position={springPosition.position}
      rotation={springRotation.rotation}
      onClick={() => {
        const keys = [...letterPositions.keys()]
        setActiveLetter(keys[Math.floor(Math.random() * keys.length)])
      }}
    >
      <animated.group ref={cube}>
        <mesh geometry={cubes.cube.geometry} position={[0, 0, 0]} scale={1}>
          <meshStandardMaterial
            color='#3e384f'
            emissive='#271d2a'
            roughness={200}
            side={THREE.FrontSide}
          />
        </mesh>

        <mesh geometry={cubes.letters.geometry} position={[0, 0, 0]} scale={1}>
          <meshStandardMaterial
            color='#856e88'
            emissive='#493a3a'
            roughness={1}
            side={THREE.FrontSide}
          />
        </mesh>
      </animated.group>
    </animated.group>
  )
}

const Cubes = ({ stage }) => {
  const [cubes, setCubes] = useState([])
  const cubesGroup = useRef()

  useFrame(() => {
    const pct =
      (window.scrollY - stage.boundingRect.top) / stage.boundingRect.height
    cubesGroup.current.position.y = pct * 15
  })

  useEffect(() => {
    let cubesData = []

    switch (stage.id) {
      case 1:
        cubesData = [
          {
            letter: isMobile ? 'M' : 'M',
            position: isMobile ? [0.5, 6, -1] : [0.5, 6, -1]
          },
          {
            letter: isMobile ? 'I' : 'I',
            position: isMobile ? [3, -6, -2] : [3, -6, -2]
          },
          {
            letter: isMobile ? 'L' : 'L',
            position: isMobile ? [-7, -4.1, -1.5] : [-7, -4.1, -1.5]
          },
          {
            letter: isMobile ? 'A' : 'A',
            position: isMobile ? [-2, 4.1, -0.4] : [-2, 4.1, -0.4]
          },
          {
            letter: isMobile ? 'E' : 'E',
            position: isMobile ? [-3, -4, -2.2] : [-3, -4, -2.2]
          },
          {
            letter: isMobile ? 'O' : 'O',
            position: isMobile ? [4.5, -1, 0] : [4.5, -1, 0]
          }
        ]
        break

      case 1.5:
        cubesData = [
          {
            letter: isMobile ? 'L' : 'L',
            position: isMobile ? [0.2, 1, 0] : [1.5, 2, 0]
          },
          {
            letter: isMobile ? 'E' : 'E',
            position: isMobile ? [0, -2, 0] : [1.5, -1, 0]
          },
          {
            letter: isMobile ? 'O' : 'O',
            position: isMobile ? [-0.4, -5, 0] : [1.5, -4, 0]
          },
          {
            letter: isMobile ? 'M' : 'M',
            position: isMobile ? [-20, 16, 0] : [-4.5, -1, 0]
          },
          {
            letter: isMobile ? 'A' : 'A',
            position: isMobile ? [-20, -16, 0] : [-1.5, -1, 0]
          },
          {
            letter: isMobile ? 'L' : 'L',
            position: isMobile ? [20, -16, 0] : [4.5, -1, 0]
          }
        ]
        break

      case 2:
        cubesData = [
          {
            letter: isMobile ? 'M' : 'M',
            position: isMobile ? [0.5, 6, -1] : [0.5, 6, -1]
          },
          {
            letter: isMobile ? 'I' : 'I',
            position: isMobile ? [3, -6, -2] : [3, -6, -2]
          },
          {
            letter: isMobile ? 'L' : 'L',
            position: isMobile ? [-7, -4.1, -1.5] : [-7, -4.1, -1.5]
          },
          {
            letter: isMobile ? 'A' : 'A',
            position: isMobile ? [-2, 4.1, -0.4] : [-2, 4.1, -0.4]
          },
          {
            letter: isMobile ? 'E' : 'E',
            position: isMobile ? [-3, -4, -2.2] : [-3, -4, -2.2]
          },
          {
            letter: isMobile ? 'O' : 'O',
            position: isMobile ? [4.5, -1, 0] : [4.5, -1, 0]
          }
        ]
        break

      case 2.5:
        cubesData = [
          {
            letter: isMobile ? 'M' : 'E',
            position: isMobile ? [0.2, 1, 0] : [-3, 0, 0]
          },
          {
            letter: isMobile ? 'I' : 'M',
            position: isMobile ? [1, -2, 0] : [-3, -3, 0]
          },
          {
            letter: isMobile ? 'A' : 'I',
            position: isMobile ? [-0.4, -5, 0] : [0, -3, 0]
          },
          {
            letter: 'L',
            position: isMobile ? [-20, 18, 0] : [3, -3, 0]
          },
          {
            letter: 'A',
            position: isMobile ? [-20, -16, 0] : [-20, -16, 0]
          },
          {
            letter: 'A',
            position: isMobile ? [25, -20, -2] : [25, -20, -2]
          }
        ]
        break

      case 3:
        cubesData = [
          {
            letter: isMobile ? 'M' : 'M',
            position: isMobile ? [0.5, 6, -1] : [0.5, 6, -1]
          },
          {
            letter: isMobile ? 'I' : 'I',
            position: isMobile ? [3, -6, -2] : [3, -6, -2]
          },
          {
            letter: isMobile ? 'L' : 'L',
            position: isMobile ? [-7, -4.1, -1.5] : [-7, -4.1, -1.5]
          },
          {
            letter: isMobile ? 'A' : 'A',
            position: isMobile ? [-2, 4.1, -0.4] : [-2, 4.1, -0.4]
          },
          {
            letter: isMobile ? 'E' : 'E',
            position: isMobile ? [-3, -4, -2.2] : [-3, -4, -2.2]
          },
          {
            letter: isMobile ? 'O' : 'O',
            position: isMobile ? [4.5, -1, 0] : [4.5, -1, 0]
          }
        ]
        break

      case 3.5:
        cubesData = [
          {
            letter: isMobile ? 'E' : 'M',
            position: isMobile ? [-1, 3, 0] : [-4.5, -4, 0]
          },
          {
            letter: isMobile ? 'L' : 'I',
            position: isMobile ? [0, 0, 0] : [-1.5, -5, 0]
          },
          {
            letter: isMobile ? 'L' : 'L',
            position: isMobile ? [0.65, -3, 0] : [1.5, -3.5, 0]
          },
          {
            letter: isMobile ? 'A' : 'A',
            position: isMobile ? [0, -6, 0] : [4.5, -3, 0]
          },
          {
            letter: isMobile ? 'A' : 'A',
            position: isMobile ? [-16, -9, 0] : [-16, -9, 0]
          },
          {
            letter: isMobile ? 'A' : 'A',
            position: isMobile ? [20, 9, 0] : [20, 9, 0]
          }
        ]
        break
    }

    setCubes(cubesData)
  }, [stage])

  return (
    <animated.group ref={cubesGroup}>
      {cubes.map((c, i) => (
        <Cube
          key={`cube-${i}`}
          stage={stage}
          letter={c.letter}
          position={c.position}
        />
      ))}
    </animated.group>
  )
}

const Particle = ({ model, show }) => {
  const mesh = useRef()
  const rdX = useMemo(() => 1 - Math.random() * 2, [])
  const rdY = useMemo(() => 1 - Math.random() * 2, [])
  const rdZ = useMemo(() => 1 - Math.random() * 2, [])
  const rd = useMemo(() => Math.random(), [])

  const rdScale = useMemo(() => 0.2 + Math.random() * 1.75, [])

  const spring = useSpring({
    position: [
      show
        ? Math.cos(rd * Math.PI * 2) * (1 + 15 * rd)
        : Math.cos(rd * Math.PI * 2) * 50,
      show
        ? Math.sin(rd * Math.PI * 2) * (1 + 15 * rd)
        : Math.sin(rd * Math.PI * 2) * 50,
      rdZ * 5
    ],
    config: config.gentle
  })

  useFrame(state => {
    mesh.current.rotation.x +=
      Math.sin(state.clock.elapsedTime * 0.05) * 0.05 * rdX
    mesh.current.rotation.y +=
      Math.sin(state.clock.elapsedTime * 0.02) * 0.02 * rdY
  })

  return (
    <group>
      <animated.mesh
        ref={mesh}
        rotation={[rdX, rdY, 0]}
        position={spring.position}
        geometry={model}
        scale={rdScale}
      >
        <meshStandardMaterial
          color='#3e384f'
          emissive='#271d2a'
          roughness={200}
          side={THREE.FrontSide}
        />
      </animated.mesh>
    </group>
  )
}

const Particles = ({ show, models, stage }) => {
  const group = useRef()
  const particles = useMemo(
    () =>
      [...new Array(20)].map(p => ({
        color: 'red'
      })),
    []
  )

  useFrame(() => {
    const pct =
      (window.scrollY - stage.boundingRect.top) / stage.boundingRect.height
    group.current.position.y = pct * 20
  })

  return (
    <animated.group ref={group}>
      {particles.map((p, i) => (
        <Particle
          key={`particle-${i}`}
          model={models.triangle.geometry}
          show={show}
        />
      ))}
    </animated.group>
  )
}

const Clouds = ({ position, models, stage }) => {
  const group = useRef()
  const meshFireA = useRef()
  const meshFireB = useRef()
  const meshFireC = useRef()
  const meshFireD = useRef()

  useFrame((state, delta) => {
    meshFireA.current.position.x +=
      Math.sin(state.clock.elapsedTime * 0.5) * 0.0065
    meshFireA.current.position.y +=
      Math.sin(state.clock.elapsedTime * 0.5) * 0.0025

    meshFireB.current.position.x +=
      Math.sin(state.clock.elapsedTime * 0.5) * -0.005
    meshFireB.current.position.y +=
      Math.sin(state.clock.elapsedTime * 0.5) * -0.002

    meshFireC.current.position.x +=
      Math.sin(state.clock.elapsedTime * 0.5) * -0.0035
    meshFireC.current.position.y +=
      Math.sin(state.clock.elapsedTime * 0.5) * -0.0029

    meshFireD.current.position.x +=
      Math.sin(state.clock.elapsedTime * 0.5) * 0.0076
    meshFireD.current.position.y +=
      Math.sin(state.clock.elapsedTime * 0.5) * 0.0042
  })

  const spring = useSpring({
    position: position,
    config: config.wobbly
  })

  const offset = {
    1: 0,
    '1.5': 0,
    2: 0,
    '2.5': 0,
    3: 0,
    '3.5': 0
  }

  useFrame(() => {
    const pct =
      (window.scrollY - stage.boundingRect.top) / stage.boundingRect.height
    group.current.position.y = offset[stage.id] + pct * 20
  })

  return (
    <animated.group ref={group} position={spring.position}>
      <mesh
        ref={meshFireA}
        geometry={models.cloudA.geometry}
        position={[1, -1, 0.5]}
        scale={3}
      >
        <meshStandardMaterial
          color='#3e384f'
          emissive='#271d2a'
          roughness={200}
          side={THREE.FrontSide}
        />
      </mesh>
      <mesh
        ref={meshFireB}
        geometry={models.cloudB.geometry}
        position={[1.76, -2, -0.5]}
        scale={3}
      >
        <meshStandardMaterial
          color='#514253'
          emissive='#493a3a'
          roughness={200}
          side={THREE.FrontSide}
        />
      </mesh>
      <mesh
        ref={meshFireC}
        geometry={models.cloudC.geometry}
        position={[0, 0, 1]}
        scale={3}
      >
        <meshStandardMaterial
          color='#3e384f'
          emissive='#271d2a'
          roughness={200}
          side={THREE.FrontSide}
        />
      </mesh>
      <mesh
        ref={meshFireD}
        geometry={models.cloudSmall.geometry}
        position={[0, -2, 0.25]}
        scale={3}
      >
        <meshStandardMaterial
          color='#3e384f'
          emissive='#271d2a'
          roughness={200}
          side={THREE.FrontSide}
        />
      </mesh>
    </animated.group>
  )
}

const Fire = ({ models, position, stage }) => {
  const group = useRef()
  const meshFireA = useRef()
  const meshFireB = useRef()

  const offset = {
    1: 30,
    '1.5': 30,
    2: -10,
    '2.5': -10,
    3: 30,
    '3.5': 30
  }

  useFrame((state, delta) => {
    state.camera.lookAt(0, 0, 0)

    meshFireA.current.position.x = Math.sin(state.clock.elapsedTime * 2) * 0.5
    meshFireB.current.position.x = -Math.sin(state.clock.elapsedTime * 2) * 0.5

    const pct =
      (window.scrollY - stage.boundingRect.top) / stage.boundingRect.height
    group.current.position.y = offset[stage.id] + pct * 20
  })

  const spring = useSpring({
    position: position,
    config: config.wobbly
  })

  return (
    <animated.group ref={group} position={spring.position}>
      <mesh
        ref={meshFireA}
        geometry={models.fireA.geometry}
        position={[0, 0, 0.5]}
        scale={3}
      >
        <meshStandardMaterial
          color='#3e384f'
          emissive='#271d2a'
          roughness={200}
          side={THREE.FrontSide}
        />
      </mesh>
      <mesh
        ref={meshFireB}
        geometry={models.fireB.geometry}
        position={[0, 0, -0.5]}
        scale={3}
      >
        <meshStandardMaterial
          color='#514253'
          emissive='#493a3a'
          roughness={200}
          side={THREE.FrontSide}
        />
      </mesh>
    </animated.group>
  )
}

export const ClusterCannon = ({ stage }) => {
  const assets = useGLTF(require('../assets.glb'))

  const models = useMemo(
    () => ({
      fireA: assets.nodes.fireA,
      fireB: assets.nodes.fireB,
      cloudA: assets.nodes.cloudA,
      cloudB: assets.nodes.cloudB,
      cloudC: assets.nodes.cloudC,
      cloudSmall: assets.nodes.cloudSmall,
      triangle: assets.nodes.triangle
    }),
    [assets]
  )

  // function Bg({ colorA, colorB }) {
  //   const mesh = useRef()
  //   useFrame((state, delta) => {
  //     mesh.current.rotation.x = mesh.current.rotation.y = mesh.current.rotation.z += delta
  //   })
  //   return (
  //     <mesh ref={mesh} scale={20}>
  //       <sphereGeometry args={[1, 64, 64]} />
  //       <LayerMaterial
  //         color='#242029'
  //         lighting='physical'
  //         transmission={1}
  //         attach='material'
  //         side={THREE.BackSide}
  //       >
  //         <Depth
  //           colorA={colorB}
  //           colorB={colorA}
  //           alpha={0.5}
  //           mode='normal'
  //           near={0}
  //           far={300}
  //           origin={[100, 100, 100]}
  //         />
  //       </LayerMaterial>
  //     </mesh>
  //   )
  // }

  return (
    <div className={styles.container}>
      {process.env.NODE_ENV !== 'production' && <Debug stage={stage} />}
      <Canvas
        dpr={2}
        gl={{ alpha: true, stencil: false, depth: false, antialias: false }}
        onCreated={state => (state.gl.toneMappingExposure = 1.5)}
      >
        {/* <Leva hidden /> */}
        {process.env.NODE_ENV !== 'production' && <Stats />}

        {/* <Bg colorA="#0d4848" colorB="#771ca0" /> */}

        <PerspectiveCamera
          position={[-6, 7, 20]}
          fov={35}
          near={1}
          far={1000}
          makeDefault
        />
        <ambientLight intensity={0.75} />
        <spotLight
          position={[20, 20, 25]}
          penumbra={1}
          angle={0.2}
          color='red'
        />
        <directionalLight position={[0, 5, -4]} intensity={4} color='#a16d8e' />
        <directionalLight position={[0, -15, -0]} intensity={4} color='green' />

        <Fire
          models={models}
          position={[[2, 2.5].includes(stage.id) ? 0 : -20, 0, 3]}
          stage={stage}
        />
        <Clouds models={models} position={[0, 0, 3]} stage={stage} />
        <Particles
          show={[1.5, 2.5].includes(stage.id)}
          models={models}
          stage={stage}
        />
        <Cubes stage={stage} />

        {/* <OrbitControls /> */}

        <Environment files={require('./adamsbridge.hdr')} />
        <EffectComposer multisampling={0}>
          <Ssao
            samples={6}
            radius={1}
            intensity={20}
            luminanceInfluence={0.1}
            color='#61416e'
          />
          <Ssao
            samples={6}
            radius={0.03}
            intensity={10}
            luminanceInfluence={0.6}
            color='#61416e'
          />
          <Noise opacity={0.075} />
        </EffectComposer>
      </Canvas>
    </div>
  )
}

const Debug = ({ stage }) => {
  const pctRef = useRef()
  const animationFrame = useRef()

  function iteration() {
    const pct =
      (window.scrollY - stage.boundingRect.top) / stage.boundingRect.height
    pctRef.current.innerHTML = Math.round(pct * 1000) / 10
    animationFrame.current = window.requestAnimationFrame(iteration)
  }

  useEffect(() => {
    window.cancelAnimationFrame(animationFrame.current)
    if (pctRef) {
      window.requestAnimationFrame(iteration)
    }
  }, [pctRef, stage])

  return (
    <div className={styles.debug}>
      Stage: {stage.id} Position: {Math.round(stage.boundingRect.top)}px{' '}
      Percentage: <span ref={pctRef} />%
    </div>
  )
}
